import * as React from "react";
import { useContext, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useExhConsents } from "components/pages/patients/patients/hooks/useExhConsents";
import { useExhPatient } from "components/pages/patients/patients/hooks/useExhPatient";
import { useExhWbcResults } from "components/pages/patients/patients/hooks/useExhWbcResults";
import { ErrorNoPatient } from "lib/errorHandler/components/errorNoPatient";

import { PatientContextType } from "./types";

const defaultContext: PatientContextType = {
  patient: null,
  loading: true,
  resultsLoading: true,
  device: null,
  refetch: async () => {},
};

export const PatientContext = React.createContext<PatientContextType>(defaultContext);

export const PatientProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { id } = useParams<{ id: string }>();

  const { patient: exhPatient, loading: patientLoading, error, refetch: refetchPatient } = useExhPatient(id);
  // Use the stable patient_id UUID (not the document ID) to query consent records
  const { consents, loading: consentsLoading, refetch: refetchConsents } = useExhConsents(exhPatient?.patientId);

  const {
    results: testResults,
    loading: resultsLoading,
    refetch: refetchTestResults,
  } = useExhWbcResults(exhPatient?.deviceMeasurementIds);

  // Page header is ready once the patient document and consents resolve; results-table loads in the background
  const loading = patientLoading || consentsLoading;

  const patient = useMemo(() => {
    if (!exhPatient || !consents) return null;
    return {
      ...exhPatient,
      consent: consents,
      testResults,
    };
  }, [exhPatient, consents, testResults]);

  const is404 = useMemo(() => {
    return !loading && !patient && error?.message === "Patient not found";
  }, [loading, patient, error]);

  const refetch = React.useCallback(async () => {
    await Promise.all([refetchPatient(), refetchConsents(), refetchTestResults()]);
  }, [refetchPatient, refetchConsents, refetchTestResults]);

  const context = useMemo<PatientContextType>(() => {
    return {
      patient,
      loading,
      resultsLoading,
      device: null,
      refetch,
    };
  }, [loading, resultsLoading, patient, refetch]);

  return <PatientContext.Provider value={context}>{is404 ? <ErrorNoPatient /> : children}</PatientContext.Provider>;
};

export const usePatient = (): PatientContextType => useContext(PatientContext);
